/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, Download, FileSpreadsheet, FileText, Calendar, Filter, ChevronRight, ChevronLeft } from "lucide-react";
import apiService from "@/services/apiService";
import { DocumentViewPopup } from "@/components/document-view-popup";
import { getUserInfo } from "@/services/userService";

const documentTypeNames = {
  "107": "107 ",
  "111": "111 ",
  "116_3": "116_3 ",
  "117": "117 ",
};

interface PoliceData {
  circle: string;
  stations: Station[];
}

interface Station {
  code: string;
  district: string;
  documents: {
    "107": number;
    "111": number;
    "116_3": number;
    "117": number;
  };
  id: string;
  name: string;
  nameEn: string;
  bondAmount111: number;
  bondAmount117: number;
}

export default function PsWiseReport() {
  const [policeData, setPoliceData] = useState<PoliceData[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [selectedStation, setSelectedStation] = useState("");
  const [showDocumentTable, setShowDocumentTable] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);

  const [docCurrentPage, setDocCurrentPage] = useState(1);
  const [docItemsPerPage, setDocItemsPerPage] = useState(10);
  const [docTotalPages, setDocTotalPages] = useState(0);
  const [docTotal, setDocTotal] = useState(0);
  const [docStartIndex, setDocStartIndex] = useState(0);
  const [docEndIndex, setDocEndIndex] = useState(0);

  // Date range state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateBasis, setDateBasis] = useState<"created" | "updated">("created");
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setDocItemsPerPage(10);
    const queryParams: any = {};

    // Add date range to query params if selected
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;
    if (startDate || endDate) queryParams.dateBasis = dateBasis;
    const userInfo: any = getUserInfo();
    const userId = userInfo ? (userInfo.id ? userInfo.id : null) : null;
    if (userId) queryParams["user_id"] = userId;

    const psWiseReports: any = await apiService.get(`reports/get-ps-wise-report`, queryParams);
    setPoliceData(psWiseReports);
  };

  const handleCloseDocumentTable = () => {
    setShowDocumentTable(false);
    setDocCurrentPage(1);
    setSelectedDocuments([]);
  };

  const handleDateRangeApply = () => {
    init(); // Refresh data with new date range
    setShowDateFilter(false);
  };

  const handleDateRangeClear = async () => {
    setStartDate("");
    setEndDate("");
    setShowDateFilter(false);
    const psWiseReports: any = await apiService.get(`reports/get-ps-wise-report`);
    setPoliceData(psWiseReports);
  };

  // Calculate totals for overview cards
  const totalStations = policeData.reduce((acc, circle) => acc + circle.stations.length, 0);
  const totalDocuments = policeData.reduce((acc, circle) => acc + circle.stations.reduce((stationAcc: any, station: any) => stationAcc + Object.values(station.documents).reduce((docAcc: number, count) => docAcc + (count as number), 0), 0), 0);

  const formatCapitalize = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getBondAmount = (doc: any) => {
    if (doc.crpc129Notice && doc.crpc129Notice.boundDownOrder && doc.crpc129Notice.boundDownOrder.bondAmount) return doc.crpc129Notice.boundDownOrder.bondAmount;
    if (doc.bNSL129Notice && doc.bNSL129Notice.boundDownOrder && doc.bNSL129Notice.boundDownOrder.bondAmount) return doc.bNSL129Notice.boundDownOrder.bondAmount;
    if (doc.crpcBondOrder && doc.crpcBondOrder.boundDownOrder && doc.crpcBondOrder.boundDownOrder.bondAmount) return doc.crpcBondOrder.boundDownOrder.bondAmount;
  };

  const handleDocPageChange = (page: number) => {
    setDocCurrentPage(page);
    handleDocumentClick(selectedStation, Object.keys(documentTypeNames).find((key) => documentTypeNames[key as keyof typeof documentTypeNames] === selectedDocType) || "107", page);
  };

  const handleDocumentClick = async (stationName: string, docType: string, page: number = 1) => {
    const queryParams: any = {
      limit: docItemsPerPage,
      page: page,
      policeStation: stationName,
    };

    // Add date range to document query params
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    const psDocumentsResponse: any = await apiService.get(`/documents`, queryParams, {});
    const documents: any = psDocumentsResponse.data.map((doc: any) => ({
      id: doc.id,
      caseType: doc.generalDiary.caseType,
      officer: doc.generalDiary.entryForOfficer,
      date: new Date(doc.updatedAt).toLocaleDateString("en-IN"),
      bondAmount: getBondAmount(doc),
      riskLevel: formatCapitalize(doc.generalDiary.riskLevel),
      data: doc,
    }));

    setSelectedDocuments(documents);
    setSelectedDocType(documentTypeNames[docType as keyof typeof documentTypeNames]);
    setSelectedStation(stationName);

    // Set pagination data
    setDocTotalPages(psDocumentsResponse.pagination.totalPages);
    setDocStartIndex(psDocumentsResponse.pagination.limit * (psDocumentsResponse.pagination.page - 1));
    setDocEndIndex(psDocumentsResponse.pagination.limit * psDocumentsResponse.pagination.page);
    setDocTotal(psDocumentsResponse.pagination.total);
    setDocCurrentPage(page);

    setShowDocumentTable(true);
  };

  const handleViewDocument = (document: any) => {
    console.log(document);
    setSelectedDocument(document);
    setShowDocumentDetail(true);
  };

  // Export functions
  const convertToCSV = (data: any[]) => {
    const headers = ["S. No.", "Circle", "Police Station", "CrPC 107/BNSS 126", "CrPC 111/BNSS 130", "Bond Amt 111 (INR)", "CrPC 117/BNSS 136", "Bond Amt 117 (INR)", "CrPC 116(3)/BNSS 135(3)", "Total Documents"];

    const rows = data.map((row, index) => [index + 1, row.circle, row.stationName, row.documents["107"], row.documents["111"], row.bondAmount111 || 0, row.documents["117"], row.bondAmount117 || 0, row.documents["116_3"], row.totalDocs]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))].join("\n");

    return csvContent;
  };

  const prepareExportData = () => {
    const exportData: any[] = [];

    policeData.forEach((circle: any) => {
      circle.stations.forEach((station: any) => {
        const totalDocs = Object.values(station.documents).reduce((acc: number, count) => acc + (count as number), 0);
        exportData.push({
          circle: circle.circle,
          stationName: station.name,
          documents: station.documents,
          totalDocs,
          bondAmount111: station.bondAmount111 || 0,
          bondAmount117: station.bondAmount117 || 0,
        });
      });
    });

    return exportData;
  };

  const exportToCSV = () => {
    const exportData = prepareExportData();
    const csvContent = convertToCSV(exportData);

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `police_station_report_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const exportData = prepareExportData();

    // Create Excel-compatible HTML table
    let htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Police Station Report</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>S. No.</th>
              <th>Circle</th>
              <th>Police Station</th>
              <th>CrPC 107/BNSS 126</th>
              <th>CrPC 111/BNSS 130</th>
              <th>Bond Amt 111 (INR)</th>
              <th>CrPC 117/BNSS 136</th>
              <th>Bond Amt 117 (INR)</th>
              <th>CrPC 116(3)/BNSS 135(3)</th>
              <th>Total Documents</th>
            </tr>
          </thead>
          <tbody>
    `;

    exportData.forEach((row, index) => {
      htmlContent += `
        <tr>
          <td>${index + 1}</td>
          <td>${row.circle}</td>
          <td>${row.stationName}</td>
          <td>${row.documents["107"]}</td>
          <td>${row.documents["111"]}</td>
          <td>${row.bondAmount111 || 0}</td>
          <td>${row.documents["117"]}</td>
          <td>${row.bondAmount117 || 0}</td>
          <td>${row.documents["116_3"]}</td>
          <td>${row.totalDocs}</td>
        </tr>
      `;
    });

    htmlContent += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `police_station_report_${new Date().toISOString().split("T")[0]}.xls`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  let dataIndex: number = 0;
  let total107 = 0;
  let total111 = 0;
  let total116 = 0;
  let total117 = 0;
  let totalBond111 = 0;
  let totalBond117 = 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Police Station Analytics</h1>
              <p className="text-muted-foreground">Document management overview by circles and stations</p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-sm">
                {totalStations} Stations
              </Badge>
              <Badge variant="outline" className="text-sm">
                {totalDocuments} Documents
              </Badge>

              {/* Date Range Filter */}
              <DropdownMenu open={showDateFilter} onOpenChange={setShowDateFilter}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 h-7 cursor-pointer">
                    <Calendar className="h-4 w-4" />
                    {startDate || endDate ? <span className="text-xs">{startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate}</span> : "Date Range"}
                    <Filter className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-4">
                  <div className="space-y-4">
                    <div className="text-sm font-medium">Filter by Date Range</div>
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">Filter on</div>
                      <div className="flex gap-4 text-xs">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="dateBasis" checked={dateBasis === "created"} onChange={() => setDateBasis("created")} />
                          Created
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="dateBasis" checked={dateBasis === "updated"} onChange={() => setDateBasis("updated")} />
                          Updated
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start-date" className="text-xs">
                          Start Date
                        </Label>
                        <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end-date" className="text-xs">
                          End Date
                        </Label>
                        <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8" />
                      </div>
                    </div>
                    <div className="flex justify-between gap-2">
                      <Button variant="outline" size="sm" onClick={handleDateRangeClear} className="cursor-pointer">
                        Clear
                      </Button>
                      <Button variant="secondary" size="sm" onClick={handleDateRangeApply} className="cursor-pointer">
                        Apply Filter
                      </Button>
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 h-7 cursor-pointer">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToCSV} className="flex items-center gap-2 cursor-pointer">
                    <FileText className="h-4 w-4" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToExcel} className="flex items-center gap-2 cursor-pointer">
                    <FileSpreadsheet className="h-4 w-4" />
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        <div className="space-y-6">
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-black font-semibold py-2 px-2">S. No.</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2">Circle</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2">Police Station</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center">CrPC 107/ BNSS 126</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center">CrPC 111/ BNSS 130</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center text-blue-700">Bond Amt (111)</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center">CrPC 117/ BNSS 136</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center text-blue-700">Bond Amt (117)</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center">CrPC 116(3)/ BNSS 135(3)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policeData.map((circle: any) => {
                return circle.stations.map((station: any) => {
                  total107 += station.documents["107"];
                  total111 += station.documents["111"];
                  total116 += station.documents["116_3"];
                  total117 += station.documents["117"];
                  totalBond111 += station.bondAmount111 || 0;
                  totalBond117 += station.bondAmount117 || 0;

                  dataIndex++;
                  return (
                    <TableRow key={station.id} className="hover:bg-gray-50">
                      <TableCell className="py-1 px-2 text-gray-600">{dataIndex}</TableCell>
                      <TableCell className="py-1 px-2 text-gray-600">{circle.circle}</TableCell>
                      <TableCell className="py-1 px-2 text-gray-600">{station.name}</TableCell>
                      <TableCell className="py-1 px-2 text-center">
                        <Button variant="outline" size="sm" className="w-full h-6 px-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors bg-transparent cursor-pointer" onClick={() => handleDocumentClick(station.name, "107")}>
                          {station.documents["107"]}
                        </Button>
                      </TableCell>
                      <TableCell className="py-1 px-2 text-center">
                        <Button variant="outline" size="sm" className="w-full h-6 px-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors bg-transparent cursor-pointer" onClick={() => handleDocumentClick(station.name, "111")}>
                          {station.documents["111"]}
                        </Button>
                      </TableCell>
                      <TableCell className="py-1 px-2 text-center text-blue-700">
                        {station.bondAmount111 > 0 ? `₹${station.bondAmount111.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell className="py-1 px-2 text-center">
                        <Button variant="outline" size="sm" className="w-full h-6 px-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors bg-transparent cursor-pointer" onClick={() => handleDocumentClick(station.name, "117")}>
                          {station.documents["117"]}
                        </Button>
                      </TableCell>
                      <TableCell className="py-1 px-2 text-center text-blue-700">
                        {station.bondAmount117 > 0 ? `₹${station.bondAmount117.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell className="py-1 px-2 text-center">
                        <Button variant="outline" size="sm" className="w-full h-6 px-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors bg-transparent cursor-pointer" onClick={() => handleDocumentClick(station.name, "116_3")}>
                          {station.documents["116_3"]}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
              <TableRow key={"total"} className="bg-gray-50 font-semibold">
                <TableCell className="py-1 px-2" />
                <TableCell className="py-1 px-2 text-gray-800">Total</TableCell>
                <TableCell className="py-1 px-2" />
                <TableCell className="py-1 px-2 text-center">{total107}</TableCell>
                <TableCell className="py-1 px-2 text-center">{total111}</TableCell>
                <TableCell className="py-1 px-2 text-center text-blue-700">
                  {totalBond111 > 0 ? `₹${totalBond111.toLocaleString("en-IN")}` : "-"}
                </TableCell>
                <TableCell className="py-1 px-2 text-center">{total117}</TableCell>
                <TableCell className="py-1 px-2 text-center text-blue-700">
                  {totalBond117 > 0 ? `₹${totalBond117.toLocaleString("en-IN")}` : "-"}
                </TableCell>
                <TableCell className="py-1 px-2 text-center">{total116}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Document Table Dialog */}
      <Dialog open={showDocumentTable} onOpenChange={handleCloseDocumentTable}>
        <DialogContent className="max-h-[95vh] bg-white flex flex-col w-[95%] overflow-y-auto" style={{ maxWidth: "1200px" }}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedDocType} Documents - {selectedStation}
            </DialogTitle>
            <DialogDescription>{docTotal} documents found</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Case Type</TableHead>
                  <TableHead>Officer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Bond Amount</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead>View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedDocuments.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.caseType}</TableCell>
                    <TableCell>{doc.officer}</TableCell>
                    <TableCell>{doc.date}</TableCell>
                    <TableCell>₹{doc.bondAmount?.toLocaleString() ?? "-"}</TableCell>
                    <TableCell>
                      <Badge variant={doc.riskLevel === "High" ? "destructive" : doc.riskLevel === "Medium" ? "default" : "secondary"}>{doc.riskLevel}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button className="cursor-pointer" size="sm" variant="ghost" onClick={() => handleViewDocument(doc.data)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {selectedDocuments.length === 0 && <div className="text-center py-8 text-gray-500">No documents found.</div>}

            {/* Pagination - Add this section */}
            {docTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Showing {docStartIndex + 1} to {Math.min(docEndIndex, docTotal)} of {docTotal} documents
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDocPageChange(docCurrentPage - 1)} disabled={docCurrentPage === 1} className="border-gray-300">
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: docTotalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        return page === 1 || page === docTotalPages || Math.abs(page - docCurrentPage) <= 1;
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                          <Button variant={docCurrentPage === page ? "default" : "outline"} size="sm" onClick={() => handleDocPageChange(page)} className={docCurrentPage === page ? "bg-black text-white" : "border-gray-300"}>
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button variant="outline" size="sm" onClick={() => handleDocPageChange(docCurrentPage + 1)} disabled={docCurrentPage === docTotalPages} className="border-gray-300">
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Detail Dialog */}
      {selectedDocument && showDocumentDetail && <DocumentViewPopup open={showDocumentDetail} onOpenChange={() => setShowDocumentDetail(false)} document={selectedDocument} />}
    </div>
  );
}
